import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  Inject,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf
} from '@angular/core';
import { throwIfAlreadyLoaded } from '@eview/utils';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { AuthService } from './auth';
import { environment } from './environments/environment';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ApiErrorInterceptor } from './interceptors/error.interceptor';
import { HttpLoadingInterceptor } from './interceptors/http-loading.interceptor';
import { AppLoadingModule } from './loading/app-loading.module';
import { CORE_PROVIDERS, PlatformLanguageToken } from './services';
import { LogService } from './services/log.service';
import { SessionExpiredService } from './services/session-expired.service';
import { UserLoggedIn } from './store/actions/app.actions';
import { GetCurrentUser, LogoutSuccess } from './store/actions/auth.actions';
import { GetConfig } from './store/actions/config.actions';
import { appEffects } from './store/effects/app.effects';
import { appReducers } from './store/reducers/app.reducers';
import { AppState } from './store/states/app.state';
import { ApiServerError } from '@eview/core/store/actions/error.actions';
import { CheckNetworkStatus } from '@eview/core/store/actions/network-status.actions';
import { clearLegacyAppStorage } from './storage/app-storage.helpers';
import { storageMetaReducer } from './storage.metareducer';
import { selectNetworkStatus } from '@eview/core/store/selectors/network-status.selector';
import { GetTenantBaseUrl } from './store/actions/tenant.actions';
import { selectTenant } from './store/selectors/tenant.selector';
import { AppConfigService } from './app-config.service';

/**
 * DEBUGGING
 */
LogService.DEBUG.LEVEL_4 = !environment.production;


const configInitializer = (appConfig: AppConfigService) => {
  return () => {
    return appConfig.loadAppConfig();
  }
};

export const BASE_PROVIDERS: any[] = [
  ...CORE_PROVIDERS,
  AppConfigService,
  {
    provide: APP_INITIALIZER,
    useFactory: configInitializer,
    multi: true,
    deps: [AppConfigService]
  },
  { provide: APP_BASE_HREF, useValue: '/' },
  {
    provide: APP_INITIALIZER,
    useFactory: appInit,
    deps: [AuthService, Store],
    multi: true
  },
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ApiErrorInterceptor,
    multi: true
  },
  { provide: HTTP_INTERCEPTORS, useClass: HttpLoadingInterceptor, multi: true }
];

function initActions(auth, store) {
  const { token } = auth.init();
  if (token && auth.hasRefreshToken(token)) {
    store.dispatch(new GetCurrentUser());
    store.dispatch(new UserLoggedIn());
  }
  store.dispatch(new ApiServerError({}));
  store.dispatch(new CheckNetworkStatus());

  if (!token) {
    store.dispatch(new LogoutSuccess());
  }
}

function resolveTenantHost(): string {
  let host = typeof document !== 'undefined' ? document.location.host : '';
  if (environment.production && host === 'pbolern.com') {
    host = 'undpjtf-ireport-madagascar.leman.un-icc.cloud';
  }
  return host;
}

export function appInit(auth: AuthService, store: Store<AppState>) {
  return async () => {
    clearLegacyAppStorage();
    store.dispatch(new GetTenantBaseUrl({ frontend_url: resolveTenantHost() }));
    initActions(auth, store);

    await firstValueFrom(
      store.select(selectTenant).pipe(
        filter(tenant => !!tenant),
        take(1),
        timeout(15000),
        catchError(() => of(null))
      )
    );

    let initialStatus: boolean = false;
    store.select(selectNetworkStatus).subscribe((status) => {
      if (initialStatus) {
        initActions(auth, store);
      }
      initialStatus = status ? false : true;
    });
  };
}

@NgModule({
  imports: [
    CommonModule,
    AppLoadingModule,
    StoreModule.forRoot(appReducers, {
      metaReducers: [storageMetaReducer]
    }),
    EffectsModule.forRoot(appEffects),
    StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
    !environment.production ? StoreDevtoolsModule.instrument() : []
  ],
  exports: [AppLoadingModule]
})
export class CoreModule {
  static forRoot(configuredProviders: Array<any>): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [...BASE_PROVIDERS, ...configuredProviders]
    };
  }

  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule,
    @Inject(PlatformLanguageToken) lang: string,
    translate: TranslateService
  ) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
    translate.setDefaultLang(environment.defaultLanguage);
    if (environment.availableLanguages.find(languageCode => languageCode.toLowerCase() === lang.toLowerCase())) {
      translate.use(lang);
    }
    else {
      translate.use(environment.defaultLanguage);
    }
  }
}
